import { ClassValue } from 'clsx';
import { CSSProperties, ReactNode, isValidElement, useContext } from 'react';
import { Icon } from '@shared/components/Icon';
import { Image, ImageSource } from '@shared/components/Image';
import { Text, TextProps } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { LoadingIndicator } from '@shared/components/loading';
import { ColorThemeContext } from '@shared/styles';
import classNames from './MBListRow.module.css';

export interface MBListRowProps {
  image?: ImageSource | ReactNode;

  /*
        If set to null, no icon will be shown even if eg there is onPress
    */
  icon?: ImageSource | ReactNode;

  title: string | ReactNode;
  subtitle?: string | ReactNode;

  paddingVertical?: number;
  paddingLeft?: number;
  paddingRight?: number;

  onPress?: VoidFunction;

  /*
        If set, the row will have a light gray background. Default is false.
    */
  isSelected?: boolean;

  /*
        If set, this hides the arrow indicator (>) which is shown when
        onPress is set and no icon is set.
    */
  isHidingPressableIndicator?: boolean;

  /*
        If set, this shows a loading indicator where the arrow indicator
        would normally be.
   */
  isLoading?: boolean;

  /*
        Prevent the right icon from being wrapped in a 40x40 view if it is a
        custom component. If we want to add custom content to the right and
        not limit it in terms of size, this prop should be set.
    */
  skipWrappingIcon?: boolean;

  style?: CSSProperties;
  testID?: string;
  disabled?: boolean;
  className?: ClassValue;
}

// Keeps its MB-name for now, to distinguish from ui/ListRow. At some point,
// these will consolidate.
export function MBListRow({
  style,
  image,
  icon,
  title,
  subtitle,
  onPress,
  isHidingPressableIndicator,
  isLoading,
  isSelected,
  disabled,
  skipWrappingIcon,
  paddingVertical = 0.5,
  paddingLeft = 3,
  paddingRight = 1,
  testID,
  className,
}: MBListRowProps) {
  const pressable = !!onPress && !isLoading && !disabled;

  return (
    <View
      testID={testID}
      relative
      className={[classNames.mbListRow, className]}
      style={style}
      onClick={pressable ? onPress : undefined}
    >
      {!!isSelected && <View className={classNames.highlight} />}
      <View
        row
        center
        fullWidth
        paddingVertical={paddingVertical}
        paddingLeft={paddingLeft}
        paddingRight={paddingRight}
        className={classNames.row}
      >
        <MBListRowLeftContent content={image} />

        <View flex>
          <MBListRowText inactive={disabled}>{title}</MBListRowText>
          {subtitle && (
            <MBListRowText inactive style={{ fontSize: 15 }}>
              {subtitle}
            </MBListRowText>
          )}
        </View>

        <MBListRowRightContent
          content={isLoading ? <MenuRowLoadingIndicator /> : icon}
          isArrowIndicatorDefault={!!onPress && !isHidingPressableIndicator}
          skipWrappingIcon={isLoading || !!skipWrappingIcon}
        />
      </View>
    </View>
  );
}

interface MBListRowLeftContentProps {
  content: ImageSource | ReactNode | undefined;
}

interface MBListRowRightContentProps {
  content: ImageSource | ReactNode | undefined;
  isArrowIndicatorDefault: boolean;
  skipWrappingIcon: boolean;
}

function MBListRowLeftContent({ content }: MBListRowLeftContentProps) {
  if (!content) {
    return null;
  }

  // If the content is a custom component, return that.
  if (isValidElement(content)) {
    return (
      <View relative className={classNames.imageMargin}>
        {content}
      </View>
    );
  }

  // As default, assume that the content is an image
  // Note(fant): we don't check if what's being given as the image's
  // source is valid. But it can be safely inferred by the object type.
  return <Image source={content as ImageSource} className={classNames.image} />;
}

function MBListRowRightContent({
  content,
  isArrowIndicatorDefault,
  skipWrappingIcon,
}: MBListRowRightContentProps) {
  const colors = useContext(ColorThemeContext);
  if (!content && !isArrowIndicatorDefault) {
    return <View className={classNames.imageMargin} />;
  }

  // If there is no content and we want to show the arrow indicator (>) as default
  // (eg if we have an onPress handler), render that.
  if (!content && isArrowIndicatorDefault) {
    return (
      <View center className={classNames.image}>
        <Icon name="arrow-point-right-heavy" gridSize={3.5} color={colors.foregroundInactive} />
      </View>
    );
  }

  // If the content is a custom component and we don't want to wrap it
  if (isValidElement(content) && skipWrappingIcon) {
    return content;
  }

  // If the content is a custom component and we want to wrap it (to limit width)
  if (isValidElement(content) && !skipWrappingIcon) {
    return <View className={classNames.image}>{content}</View>;
  }

  // As default, assume that the content is an image
  return <Image source={content as ImageSource} className={classNames.image} />;
}

function MBListRowText({ children, ...otherProps }: TextProps) {
  if (!children) {
    return null;
  }

  // If the content is a custom component, return that.
  if (isValidElement(children)) {
    return children;
  }

  // If the content is text, return that. Note: the content should always
  // be text if it even gets to this point (based on the type if content).
  if (typeof children === 'string') {
    return (
      <Text size="body1" {...otherProps}>
        {children}
      </Text>
    );
  }

  return null;
}

function MenuRowLoadingIndicator() {
  return (
    <View paddingRight={1.5}>
      <LoadingIndicator />
    </View>
  );
}
